/**
 * format.ts — single source of truth for every number rendered in the UI.
 *
 * Rules:
 * - Every numeric output in components must go through one of these functions.
 * - No component may inline .toFixed(), .toLocaleString(), Math.round, or `${x}%`.
 * - null/undefined → NO_DATA ('—') for every formatter.
 */

/** The one canonical null/empty glyph. */
export const NO_DATA = '—'

/** The sentence a Google row carries when it measured "no AI Overview" on every question. */
export const NO_AI_OVERVIEW_SENTENCE =
  'Google showed no AI Overview for these questions in this period.'

/**
 * Percentage: 0.673 → "67%" (or "67.3%" with decimal:true).
 *
 * A present-but-tiny rate must not render identically to a genuine, measured zero (PRD-03):
 * `Math.round()` maps every value under 0.5% to `0`, so `pct(1/1128)` — one real observation in
 * 1,128 — read as "0%", indistinguishable from a bucket with no observations at all. `pct(0)`
 * stays "0%" here: `value` is exactly `0` there, not merely rounding to it, so the `value > 0`
 * guard below never fires for a real zero.
 *
 * **The bound must match the formatter's own precision.** This first shipped as `<0.1%`, which is
 * a *false statement* for every rate in [0.1%, 0.5%): a real 0.3% is not less than 0.1%, and
 * rendering it that way understates a measured value threefold — replacing one dishonest string
 * with another, in the task whose entire purpose was to stop the product lying. A PRD lens reviewer
 * caught it. This formatter's grain is the whole percent, so the tightest bound it can honestly
 * assert for anything rounding to zero from above is `<1%`, and that is what it says.
 *
 * **`decimal: true` has its own grain and needs its own guard.** That branch returns before the
 * whole-percent guard below ever runs, so a real, non-zero value under 0.05% — e.g. one observation
 * in several thousand — rounded through `.toFixed(1)` to `"0.0%"`, the identical "measured value
 * shown as absence" defect this function exists to remove, surviving in the one branch nobody
 * looked at when the fix above was written. The bound there is `<0.1%`, matching decimal mode's own
 * one-decimal-place grain, exactly as `<1%` matches the whole-percent grain above.
 */
export function pct(n: number | null | undefined, opts?: { decimal?: boolean }): string {
  if (n == null || !isFinite(n)) return NO_DATA
  const value = n * 100
  if (opts?.decimal) {
    const fixed = value.toFixed(1)
    if (parseFloat(fixed) === 0 && value > 0) return '<0.1%'
    return `${fixed}%`
  }
  const rounded = Math.round(value)
  if (rounded === 0 && value > 0) return '<1%'
  return `${rounded}%`
}

/**
 * Signed relative change as a fraction: 0.18 → "+18%", -0.08 → "-8%". Whole numbers like `pct`, and a
 * real change under 1% reads "+<1%" / "-<1%" — never "0%", which is only a measured zero (AC-11).
 */
export function signedPct(n: number | null | undefined): string {
  if (n == null || !isFinite(n)) return NO_DATA
  if (n === 0) return pct(0)
  return `${n > 0 ? '+' : '-'}${pct(Math.abs(n))}`
}

/** A KpiCard sparkline's serializable value format: 'pct' a 0–100 value, 'pos' an average position. */
export type SparklineFormat = 'pct' | 'pos'

/**
 * A sparkline tooltip value as its format token prints it. The sparkline carries 0–100 values and
 * `pct` takes a 0–1 rate, so the one canonical percent formatter prints them: whole numbers, "<1%"
 * for a real value under 1%, "0%" only for a zero.
 */
export function formatSparklineValue(format: SparklineFormat, v: number): string {
  return format === 'pct' ? pct(v / 100) : `#${v.toFixed(1)}`
}

/** Compact count: 1204 → "1.2K", 1_240_000 → "1.2M". Sub-1000 returns as-is. */
export function num(n: number | null | undefined): string {
  if (n == null || !isFinite(n)) return NO_DATA
  const abs = Math.abs(n)
  const sign = n < 0 ? '-' : ''
  if (abs >= 1_000_000) return `${sign}${(abs / 1_000_000).toFixed(1)}M`
  if (abs >= 1_000) return `${sign}${(abs / 1_000).toFixed(1)}K`
  return `${n}`
}

/**
 * Exact integer with thousands separators: 1234567 → "1,234,567".
 *
 * Use when the precise figure matters (call counts, token totals) and `num()`'s
 * "1.2M" compaction would hide it. The locale is pinned to `en-US` on purpose:
 * a bare `.toLocaleString()` resolves to the *runtime's* locale, so the server
 * renders "1,234,567" while a `hu-HU` browser renders "1 234 567" — a hydration
 * mismatch (React #418) that only reproduces for non-US users.
 */
export function int(n: number | null | undefined): string {
  if (n == null || !isFinite(n)) return NO_DATA
  return Math.round(n).toLocaleString('en-US')
}

/** Average position / rank: 1 → "#1.0". */
export function pos(n: number | null | undefined): string {
  if (n == null || !isFinite(n)) return NO_DATA
  return `#${n.toFixed(1)}`
}

/**
 * Margin of error in percentage points, baseline-paired with a value in
 * `KpiCard` (`docs/tasks/measurement-confidence/design-spec.md` §A):
 * 8.4 → "±8pp". Rounds to the nearest whole point — the display grain the
 * design spec's `47% ±8pp` examples use throughout.
 */
export function marginPp(n: number | null | undefined): string {
  if (n == null || !isFinite(n)) return NO_DATA
  return `±${Math.round(n)}pp`
}

export interface MarginDescription {
  /** Bare phrase, no surrounding punctuation: "7% to 50%". */
  phrase: string
}

/**
 * THE single place a confidence interval becomes words. Every surface showing a
 * visibility rate goes through this: the overview KPI, the shared-link card, the weekly
 * email and the client PDF.
 *
 * ─── Why there is no "±" here ────────────────────────────────────────────────────────
 *
 * `±` is shorthand for a SYMMETRIC interval. It is correct for opinion polls because the
 * normal approximation they use genuinely is symmetric about the estimate. Our intervals
 * come from a percentile cluster bootstrap on a bounded proportion, which is asymmetric
 * by construction, so the shorthand does not transfer. The statistical convention for an
 * asymmetric interval is to report both bounds. That is what we do.
 *
 * This replaced four accumulated threshold constants, each added to patch a case where
 * `±` had produced a false statement:
 *
 *   0% with [0%, 29%]      rendered "±14pp"  →  claims the rate could be −14%
 *   100% with [50%, 100%]  rendered "±25pp"  →  claims the rate could be 125%
 *   20% with [6.7%, 50%]   rendered "±22pp"  →  wrong at BOTH ends, [−1.7%, 41.7%]
 *   11.85% with [11.6%, 12.4%] rendered "(likely under 12%)" on a card reading 12%
 *
 * Each fix added a constant (a boundary tolerance, a symmetry tolerance, an
 * "uninformative" width cutoff), and each new constant produced the next defect at its
 * own edge. The constants were never the problem; assuming `±` transferred was. Stating
 * the interval needs no tolerance, has no edges, and cannot be wrong.
 *
 * A range covering most of the scale is self-evidently uninformative when stated as one
 * ("0% to 100%"), so that case needs no special handling either.
 *
 * Each bound goes through `pct()` (R6.3 B1), so a bound in (0, 0.5%) reads "<1%" exactly as a
 * value there does — a measured non-zero bound never prints as "0%".
 *
 * Inputs are percentages (0-100), not fractions.
 */
export function describeMargin(ciLowPct: number, ciHighPct: number): MarginDescription {
  if (!isFinite(ciLowPct) || !isFinite(ciHighPct)) return { phrase: NO_DATA }
  return { phrase: `${pct(ciLowPct / 100)} to ${pct(ciHighPct / 100)}` }
}

/**
 * `describeMargin` rendered for the slot immediately after a value, as in the KPI card's
 * `20% (7% to 50%)`. Parenthesised because it is an aside about the number, not part of
 * it.
 */
export function marginSlot(ciLowPct: number, ciHighPct: number): string {
  return `(${describeMargin(ciLowPct, ciHighPct).phrase})`
}

/**
 * Below this many observations the sample is still forming. Moved here from
 * `sample-size-label.tsx` (which re-exports it) so `intervalSegments` — and the email and PDF,
 * which cannot import a component module — share one threshold with the app (R6.3 I1).
 */
export const EARLY_THRESHOLD = 3
/** At and above this many observations the trend counts as settled — no maturity cue. */
export const SETTLED_THRESHOLD = 5

/**
 * `int(n)` plus a pluralized unit word — the one place a bare sample-size count is formatted
 * (DES-04): `countLabel(1, 'prompt')` → "1 prompt", `countLabel(1032, 'result')` → "1,032 results".
 * Regular `+s` plurals only; use `pluralise()` for an irregular noun.
 */
export function countLabel(n: number, unit: string): string {
  const word = n === 1 ? unit : `${unit}s`
  return `${int(n)} ${word}`
}

export interface IntervalSegmentsInput {
  /** `describeMargin(...).phrase`, unbracketed. Empty/absent → no range segment (a statistic with no interval). */
  readonly range: string | null | undefined
  /** The sample behind the value. `n <= 0` → no line at all (PRD-03). */
  readonly n: number
  /** Unit word, singular — "prompt", "result". */
  readonly unit: string
  /** Extra segments after the sample, e.g. the hero's "~2 checks each". */
  readonly extras?: readonly string[]
  /** `formatScope()`'s output, rendered last when present. */
  readonly scope?: string
}

/**
 * THE one interval form (R6.3 I1): the segments of the support line under a value —
 * `[range, sample, ...extras, scope?]`.
 *
 * - `range` is `describeMargin`'s bare phrase ("14% to 55%"), never bracketed.
 * - `sample` carries a maturity cue only while the sample is still forming: "Early data · 2 prompts"
 *   below `EARLY_THRESHOLD`, "Still settling · 4 prompts" below `SETTLED_THRESHOLD`, otherwise a bare
 *   "10 prompts".
 *
 * No sample (`n <= 0`) returns no segments: the value is the no-data glyph and there is no line
 * (PRD-03) — never "0 prompts". `IntervalLine` maps these segments in React (the share link, the
 * Overview, the claim page); `intervalLineText` is the text form, which both emails and the PDF
 * read.
 */
export function intervalSegments(input: IntervalSegmentsInput): string[] {
  const { range, n, unit, extras = [], scope } = input
  if (!(n > 0)) return []
  const label = countLabel(n, unit)
  const sample =
    n < EARLY_THRESHOLD
      ? `Early data · ${label}`
      : n < SETTLED_THRESHOLD
        ? `Still settling · ${label}`
        : label
  return [...(range ? [range] : []), sample, ...extras, ...(scope ? [scope] : [])]
}

/**
 * `intervalSegments` joined by " · " — the text form for email (both emails read it) and
 * react-pdf (the PDF's verdict and "By engine" read it). `''` with no sample.
 */
export function intervalLineText(input: IntervalSegmentsInput): string {
  return intervalSegments(input).join(' · ')
}

/** Dollar amount: 1286 → "$1,286". Rounds to nearest dollar. */
export function money(n: number | null | undefined): string {
  if (n == null || !isFinite(n)) return NO_DATA
  return `$${Math.round(n).toLocaleString('en-US')}`
}

/**
 * Picks the singular or plural form of a noun by count: `pluralise(1, 'page')` → `"page"`,
 * `pluralise(3, 'page')` → `"pages"`. Returns only the word — pair with `int()` for the count:
 * `` `${int(n)} ${pluralise(n, 'page')}` ``.
 *
 * Pass an explicit plural for an irregular noun: `pluralise(1, 'query', 'queries')` → `"query"`,
 * `pluralise(3, 'query', 'queries')` → `"queries"`.
 *
 * `countLabel()` above combines a count and a regular-plural unit into one string ("N scans"), but
 * only for the regular `+s` case. This is the general home for an irregular noun.
 */
export function pluralise(n: number, singular: string, plural?: string): string {
  return n === 1 ? singular : (plural ?? `${singular}s`)
}

/** Fraction: ratio(3, 3) → "3/3". */
export function ratio(a: number | null | undefined, b: number | null | undefined): string {
  if (a == null || b == null || !isFinite(a) || !isFinite(b)) return NO_DATA
  return `${a}/${b}`
}

/**
 * Signed delta: 3.2 → "+3.2", -3.2 → "-3.2", 0 → "0".
 * One decimal place.
 */
export function signed(n: number | null | undefined): string {
  if (n == null || !isFinite(n)) return NO_DATA
  const fixed = n.toFixed(1)
  return n > 0 ? `+${fixed}` : fixed
}

/**
 * Signed integer delta: 3 → "+3", -2 → "-2", 0 → "0".
 * No decimal place — for count deltas (issues, prompts, mentions).
 */
export function signedInt(n: number | null | undefined): string {
  if (n == null || !isFinite(n)) return NO_DATA
  const rounded = Math.round(n)
  return rounded > 0 ? `+${rounded}` : `${rounded}`
}

/**
 * Duration in milliseconds, Core Web Vitals style: 2450 → "2.5s", 180 → "180ms". Added for
 * `growth-engine-r3.8-crawl-diffs` · T14 (CrUX p75 LCP/INP) — checked against `docs/INVENTORY-INDEX.md`
 * first (ARC-18), no equivalent existed.
 *
 * Below 1000ms renders whole milliseconds (INP's own unit). At or above 1000ms switches to seconds
 * at one decimal place (LCP's own unit) — the same split Chrome's own Web Vitals reporting uses, so a
 * value never reads as a implausibly large millisecond count.
 */
export function ms(n: number | null | undefined): string {
  if (n == null || !isFinite(n)) return NO_DATA
  if (Math.abs(n) >= 1000) return `${(n / 1000).toFixed(1)}s`
  return `${Math.round(n)}ms`
}

/**
 * Cumulative Layout Shift score: 0.052 → "0.05". Two decimal places — CLS is a unitless score, not
 * a duration or a percentage, so neither `ms()` nor `pct()` fits it. Added alongside `ms()` for the
 * same CrUX field-data card (`growth-engine-r3.8-crawl-diffs` · T14).
 */
export function clsScore(n: number | null | undefined): string {
  if (n == null || !isFinite(n)) return NO_DATA
  return n.toFixed(2)
}
